"use client";

import { FC, HTMLAttributes, useId } from "react";
import cn from "classnames";
import RssReaderCard, { RssReaderCardType } from "./RssReaderCard";
import { Button } from "../Button";

export type RssReaderProps = {
  theme?: "white" | "gray";
  headline?: string;
  link?: {
    title: string;
    href: string;
    dld?: string;
    dlt?: string;
  };
  cards: RssReaderCardType[];
  isLoading?: boolean;
  attributes?: HTMLAttributes<HTMLElement>;
} & HTMLAttributes<HTMLElement>;

interface LoadingSpinnerProps {
  className?: string;
}

const LoadingSpinner: FC<LoadingSpinnerProps> = ({ className }) => {
  const id = useId();
  const clipPathId = `clip-${id}`;
  const pathId = `path-${id}`;
  const clipPathId2 = `clip2-${id}`;
  const pathId2 = `path2-${id}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 200 200"
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
      className={className}
    >
      <g display="none" clipPath={`url(#${clipPathId})`}>
        <ellipse
          rx="79.119057"
          ry="79.119057"
          transform="translate(100 100)"
          fill="none"
          fillRule="evenodd"
          stroke="#00c8dc"
          strokeWidth="10"
        />
        <clipPath id={clipPathId}>
          <path
            id={pathId}
            d="M100,100L35.864357,13.269443h123.110947L100,100Z"
            transform="translate(0 0.000001)"
            stroke="#3f5787"
            strokeWidth="0.5"
          />
        </clipPath>
      </g>
      <ellipse
        rx="79.119057"
        ry="79.119057"
        transform="translate(100 100)"
        fill="none"
        fillRule="evenodd"
        stroke="#d8e3e3"
        strokeWidth="20"
      />
      <g clipPath={`url(#${clipPathId2})`}>
        <ellipse
          rx="79.119057"
          ry="79.119057"
          transform="translate(100 100)"
          fill="none"
          fillRule="evenodd"
          stroke="#00a0be"
          strokeWidth="20"
        />
        <clipPath id={clipPathId2}>
          <path
            id={pathId2}
            d="M100,100L-19.158931,-172.467303l206.296365-1.245279L100,100Z"
            transform="translate(0 0.000001)"
            stroke="#3f5787"
            strokeWidth="0.5"
          />
        </clipPath>
      </g>
    </svg>
  );
};

const RssReader: FC<RssReaderProps> = ({
  theme,
  headline,
  link,
  cards,
  className,
  isLoading,
  attributes,
}) => (
  <section
    data-theme={theme}
    {...attributes}
    className={cn("bg-background", className)}
  >
    <div className="swdc-container py-10 sm:py-20">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        {headline && (
          <div className="swdc-rich-text text-headline sm:text-headline-m text-subhead-xl leading-snug font-semibold">
            {headline}
          </div>
        )}
        {link?.title && link?.href && (
          <Button
            variant="tertiary"
            size="m"
            icon="newTab"
            dld={link.dld}
            dlt={link.dlt}
            href={link.href}
            disabled={false}
            target="_blank"
            rel="noopener noreferrer"
          >
            {link.title}
          </Button>
        )}
      </div>
      {isLoading ? (
        <LoadingSpinner className="mx-auto w-32 animate-spin py-10" />
      ) : (
        cards.map((card, index) => (
          <RssReaderCard key={`${card.link}-${index}`} {...card} />
        ))
      )}
    </div>
  </section>
);

export default RssReader;
